import { Container, Graphics, Rectangle, Text } from "pixi.js"
import { EmberInvasionCandidate, EmberRunSummary, Entity, ScreenDimensions, TileSize } from "game-common/models"
import { locateInventoryActiveItem } from "game-common/item/item"
import { TextRenderer } from "../text/text_renderer"
import { disposeTextTextures } from "../text/dispose_text"
import { LpcLayered } from "../sprites/lpc_layered"
import { FlameBackdrop } from "./flame_backdrop"
import { Fader } from "./fader"
import {
    creatureMeta,
    formatDuration,
    INVADE_BREATH_MS,
    INVADE_FLARE_MS,
    invadeFlare,
    livesView,
    runSummaryButtons,
    waveRevealFrame,
    WaveRevealFrame,
} from "./run_summary_format"
import { WaveReveal } from "./run_summary_reveal"

// The ember end-of-run scorecard: a centered panel that comes up when the player falls defending
// the camp, showing how the run went and offering the way back in. Driven entirely by one payload
// from the ember controller script (Protocol.type_showRunSummary -> EmberRunSummary).
//
// It offers up to two ways on, and the difference between them is the panel's whole job to make
// plain:
//   TRY AGAIN  -- get up in this same camp, everything still standing. This is what a life BUYS,
//                 so it is offered only while the camp still has one for the player.
//   RESTART    -- abandon it for a fresh camp, which is always available.
// The lives themselves are drawn as embers under the portrait: one per life the camp granted, lit
// while unspent and burnt out once spent. When the last one goes dark "try again" goes with it --
// not greyed out but GONE, leaving one button, centered: that camp is finished with the player and
// starting again elsewhere is the only thing left to decide.
//
// Drawn here in full, in the same ember-lit language as the command wheel / role picker (warm amber
// rim, dark glass, role-coloured accents) so the whole ember UI reads as one set. Fake glows rather
// than blur filters, for the same reason they use them: filters are expensive on weak GPUs.

// The stage stretches everything vertically by SCALER (1.3), so a box that should LOOK square is
// drawn wider than tall. All the geometry below is in stage units with that already accounted for.
const SCREEN_W = ScreenDimensions.w + TileSize * 4.7
const CENTER_X = SCREEN_W / 2
// The same "visually centered" line the hero banner uses (the HUD's usable band is shorter than
// the nominal screen height).
const CENTER_Y = ScreenDimensions.h / 2 + 52

const PANEL_W = 440
const PANEL_H = 310
const PANEL_LEFT = CENTER_X - PANEL_W / 2
const PANEL_TOP = CENTER_Y - PANEL_H / 2

// Palette, shared with role_picker.ts / the ember roster.
const RIM = 0xf2a94e
const PANEL_FILL = 0x1b1512
const PANEL_ALPHA = 0.96
const CARD_FILL = 0x241b16
const CARD_RIM = 0x6b4f38
const TEXT_COLOR = 0xfff3e0
const SCRIM_COLOR = 0x06060a

// The gone-out portrait card: a cold frame, a near-black ground, and a blue-grey shadow over the
// player. Deliberately cool, where every other colour on this panel is warm.
const ASH_RIM = 0x3c4048
const ASH_FILL = 0x05070c
const ASH_SHADOW = 0x04060f
const ASH_TEXT = 0x9aa2ae
// The shadow is not flat: it thickens down the card, so the face is still legible and the body
// below it is swallowed. Top and bottom opacity of that fall, and how many bands it is drawn in
// (no gradient fills and no filters in this panel -- banded rects are what the rest of the ember
// UI uses, and they cost nothing on a weak GPU).
const ASH_SHADOW_TOP = 0.14
const ASH_SHADOW_BOTTOM = 0.95
const ASH_SHADOW_BANDS = 28

const GOLD = 0xffd23f // waves — the run's real score
const AMBER = 0xf2a94e // time survived
const EMBER = 0xff8a5c // creatures vanquished
const GREEN = 0x7be08a // forager/camp work

// Fade timing — mirrors the command wheel / role picker (~100ms each way).
const FADE_INTERVAL_MS = 5
const FADE_STEP = 0.05

// Panel-local geometry (origin = panel top-left).
const PAD = 16
const PORTRAIT_W = 104
const PORTRAIT_H = 128
const PORTRAIT_TOP = 56
// Zoom of the paperdoll inside the card, matched to the roster's framing (1.5 in a 70px card).
const PORTRAIT_SCALE = 2.2
const STATS_LEFT = PAD + PORTRAIT_W + 14
const STATS_W = PANEL_W - PAD - STATS_LEFT
const HERO_TOP = PORTRAIT_TOP
const HERO_H = 60
const TILE_TOP = 134
const TILE_H = 44
const CHIP_LABEL_Y = 186
const CHIP_TOP = 197
const CHIP_H = 16
const CHIP_GAP = 5

// The lives block sits in the left column, in the space under the portrait card.
const LIVES_LABEL_Y = 188
// The embers hang well clear of the label: a flame's tip reaches PIP_R * 1.35 above its centre,
// so a tight row here reads as the word sitting on top of the fire.
const LIVES_ROW_Y = 210
const LIVES_CAPTION_Y = 234
const PIP_R = 9
const PIP_GAP = 8

// The ember palette, warmer and redder than the panel's gold so a life reads as a coal rather
// than a coin: a red-orange body, a lighter rim, and a hot centre.
const EMBER_BODY = 0xef5524
const EMBER_RIM = 0xffab66
const EMBER_CORE = 0xffd98a

// How long the just-spent life takes to go out when the panel opens. It burns, gutters, and
// collapses into ash rather than simply appearing spent -- the player has to SEE it cost them.
const LIVES_BURN_MS = 1150
// The stretch of that, as a fraction, the ember spends still burning (and flickering) before it
// starts to go out.
const LIVES_BURN_HOLD = 0.35

// The most of the opening flourish any single frame is allowed to consume. See advanceReveal.
const REVEAL_MAX_STEP_MS = 90

// Two buttons, side by side, filling the panel's inner width. Deliberately large: this panel is a
// dead stop in the run and these are the only two things on it the player can do.
const BUTTON_H = 42
const BUTTON_GAP = 12
const BUTTON_W = (PANEL_W - PAD * 2 - BUTTON_GAP) / 2
// A lone button doesn't inherit half the panel's width -- it would read as one of a pair with the
// other one missing. Wider, and centered.
const SOLO_BUTTON_W = 240
const BUTTON_TOP = 252

// The MINOR row: the third way on ("Resurrect as Undead!"), which sits BELOW the panel's own bottom
// edge rather than inside it.
//
// Outside, because the panel has no vertical budget left -- the kill chips already stop 6px short
// of BUTTON_TOP -- and because being off the card is the clearest possible statement of rank. The
// design names the scorecard's hierarchy as the dial that stops the camp game becoming
// instrumental: this is third, smaller, cooler, and detached, and none of that is decoration.
//
// The stage is 800x600 with scale.y = SCALER, so the usable vertical band is 600/1.3 ~= 461 units
// and the panel ends at PANEL_TOP + PANEL_H = 387. This row runs to ~431, which keeps ~30 units of
// clearance -- the docked-panel height budget, which is a real limit and not a style note.
const MINOR_BUTTON_W = 262
const MINOR_BUTTON_H = 30
const MINOR_BUTTON_TOP = PANEL_H + 14
// Cool where the rest of the panel is warm: this way out does not belong to the camp. The same
// colour marks the invasion picker's cards and the "Let go" button (see gui/invasion_picker.ts), so
// the whole undead path reads as one thing.

export interface RunSummaryProps {
    // "Try again" — get up in the camp the player just died in. Always offered.
    onTryAgain?: () => void
    // "Restart" — reset the camp for a fresh run.
    onRestart?: () => void
    // "Resurrect as Undead!" — rise in a stranger's camp as one of its attackers. Offered only once
    // the run is over AND the invasion feed says some camp is open (see runSummaryButtons).
    onInvade?: () => void
}

// One drawn button, kept so its hover state can be redrawn without rebuilding the panel.
interface ButtonHandle {
    bg: Graphics
    width: number
    height: number
    primary: boolean
    // The minor row's own look. It is the INVADE button, and it is the loudest thing on the panel
    // -- see drawButton and advanceInvadeHeat.
    minor: boolean
    // Milliseconds since this button appeared, and a free-running clock for its breathing. The
    // flare is the whole point: it arrives on a panel that has been sitting still.
    flareMs?: number
    pulseMs?: number
    // An inert button is drawn, but says why rather than doing anything. Only the minor row is ever
    // this -- see runSummaryButtons.
    enabled: boolean
    hovered: boolean
}

export class RunSummary extends Container {
    private onTryAgain?: () => void
    private onRestart?: () => void
    private onInvade?: () => void

    private scrim: Graphics
    // Everything inside the panel is rebuilt per open (the numbers change), so it lives under one
    // container that can be emptied wholesale.
    private panel: Container
    private content: Container
    private flame?: FlameBackdrop
    // Anything currently wearing a mask. Masks have to be detached before the subtree is destroyed
    // (a display object left pointing at a destroyed mask crashes the next render), which is the
    // same dance the roster cards do.
    private masked: Container[] = []

    // The ways-on row(s), kept in a layer of their own so they can be rebuilt without rebuilding
    // the scorecard around them. That is not an optimisation: the invasion offer is an interval
    // FEED, so WHICH buttons exist changes while the panel is already up (see syncButtons).
    private buttonLayer?: Container
    private buttons: ButtonHandle[] = []
    // The summary the panel is currently drawn around, and the newest invasion offer, which
    // together decide the buttons. `undefined` candidates means the feed has never spoken -- see
    // runSummaryButtons, where the difference between that and an empty list is the whole rule.
    private summary?: EmberRunSummary
    private invasionCandidates?: EmberInvasionCandidate[]
    // Signature of the button set last drawn, so a feed tick that changes nothing visible doesn't
    // rebuild the row out from under a pointer that is hovering it.
    private buttonsKey?: string

    // The lives row, kept so it can be redrawn every frame while the just-spent ember burns out.
    private embers?: Graphics
    private livesLayout?: { firstCx: number; step: number; cy: number; total: number; spent: number }
    private livesBurnMs = 0
    // Sparks thrown off as the ember collapses: { x, y, vy, age, life }.
    private sparks: Array<{ x: number; y: number; vx: number; vy: number; age: number; life: number }> = []

    // The opening flourish: the wave the run reached, thrown up over the camp before the panel
    // says anything (see run_summary_reveal.ts). It runs off the same clock the panel's entrance
    // does, so the two hand over rather than queue.
    private reveal: WaveReveal
    private revealMs = 0
    private revealing = false

    private opened = false
    private fadeInFader: Fader | null = null
    private fadeOutFader: Fader | null = null

    constructor(props: RunSummaryProps = {}) {
        super()
        this.onTryAgain = props.onTryAgain
        this.onRestart = props.onRestart
        this.onInvade = props.onInvade

        // The scrim both dims the world and swallows every click, so a stray press while the panel
        // is up can't fire the player's bow into the camp behind it.
        this.scrim = new Graphics()
        this.scrim.beginFill(SCRIM_COLOR, 0.8)
        this.scrim.drawRect(-400, -400, SCREEN_W + 800, 1400)
        this.scrim.endFill()
        this.scrim.interactive = true
        this.scrim.hitArea = new Rectangle(-400, -400, SCREEN_W + 800, 1400)
        this.scrim.on("pointerdown", (event: any) => event.stopPropagation())
        this.scrim.on("pointermove", (event: any) => event.stopPropagation())
        this.scrim.on("rightclick", (event: any) => event.stopPropagation())
        this.scrim.on("contextmenu", (event: any) => {
            event.preventDefault()
            event.stopPropagation()
        })
        this.addChild(this.scrim)

        this.panel = new Container()
        this.panel.x = PANEL_LEFT
        this.panel.y = PANEL_TOP
        this.addChild(this.panel)

        this.content = new Container()
        this.panel.addChild(this.content)

        // Over the panel, and centered on the screen rather than on the panel: the number is
        // bigger than the thing it introduces.
        this.reveal = new WaveReveal()
        this.reveal.x = CENTER_X
        this.reveal.y = CENTER_Y
        this.addChild(this.reveal)

        this.visible = false
    }

    // ===== drawing =====================================================================

    // Dark "glass" body with a warm rim and a faint outer glow (a fatter low-alpha copy — cheaper
    // than a blur filter, which matters on low-end GPUs), plus a warm bar across the very top so
    // the header reads as its own band.
    private drawPanelChrome(): Graphics {
        const g = new Graphics()
        g.beginFill(RIM, 0.05)
        g.drawRoundedRect(-6, -6, PANEL_W + 12, PANEL_H + 12, 16)
        g.endFill()

        g.lineStyle(2, RIM, 0.9)
        g.beginFill(PANEL_FILL, PANEL_ALPHA)
        g.drawRoundedRect(0, 0, PANEL_W, PANEL_H, 11)
        g.endFill()

        // Header underline, fading out at both ends.
        g.lineStyle(0)
        g.beginFill(RIM, 0.35)
        g.drawRect(PAD * 2, 47, PANEL_W - PAD * 4, 1)
        g.endFill()
        return g
    }

    private addText(
        parent: Container,
        text: string,
        opts: { size: number; color?: number; bold?: boolean; alpha?: number; x: number; y: number; center?: boolean },
    ): Text {
        const builder = new TextRenderer().fontSize(opts.size).color(opts.color === undefined ? TEXT_COLOR : opts.color)
        if (opts.bold) {
            builder.bold()
        }
        const t = builder.align("center").render(text) as Text
        t.x = opts.center ? Math.round(opts.x - t.width / 2) : opts.x
        t.y = opts.y
        if (opts.alpha !== undefined) {
            t.alpha = opts.alpha
        }
        parent.addChild(t)
        return t
    }

    // Shrink a value until it fits its cell — a 12-minute run ("12:45") is a lot wider than "3".
    private addFittedValue(parent: Container, text: string, color: number, cx: number, y: number, maxW: number): Text {
        let size = 27
        let t = new TextRenderer().fontSize(size).color(color).bold().align("center").render(text) as Text
        while (t.width > maxW && size > 13) {
            disposeTextTextures(t)
            size -= 2
            t = new TextRenderer().fontSize(size).color(color).bold().align("center").render(text) as Text
        }
        t.x = Math.round(cx - t.width / 2)
        t.y = y
        parent.addChild(t)
        return t
    }

    // The player's own paperdoll, framed and lit from below by the same campfire particles the
    // roster gives the Lord — the camp's fire is what the run was about.
    //
    // `extinguished` is the last-life version of the same card: with the lives spent, the fire
    // behind the portrait is OUT. No particles, a colder frame, and the player sunk into shadow —
    // the card stops being a portrait lit by a camp and becomes what is left of one. It is the
    // same reading as the burnt embers and the missing "try again" button, said a third way.
    private buildPortrait(parent: Container, player: Entity | undefined, extinguished: boolean) {
        const card = new Container()
        card.x = PAD
        card.y = PORTRAIT_TOP
        parent.addChild(card)

        const chrome = new Graphics()
        chrome.lineStyle(1.5, extinguished ? ASH_RIM : CARD_RIM, extinguished ? 0.5 : 0.8)
        chrome.beginFill(extinguished ? ASH_FILL : 0x0a0d08, 0.95)
        chrome.drawRoundedRect(0, 0, PORTRAIT_W, PORTRAIT_H, 8)
        chrome.endFill()
        card.addChild(chrome)

        // A pixi mask belongs to exactly one display object, so the flame and the paperdoll each
        // get their own copy of the same inset rounded rect (as the roster cards do).
        const makeClip = () => {
            const clip = new Graphics()
            clip.beginFill(0xffffff)
            clip.drawRoundedRect(3, 3, PORTRAIT_W - 6, PORTRAIT_H - 6, 6)
            clip.endFill()
            return clip
        }

        // The fire behind the portrait, unless it has gone out with the last life — in which case
        // there is no backdrop at all, and animate() has nothing left to advance.
        if (!extinguished) {
            const flameClip = makeClip()
            card.addChild(flameClip)
            const flame = new FlameBackdrop(PORTRAIT_W, PORTRAIT_H)
            flame.mask = flameClip
            card.addChild(flame)
            this.flame = flame
            this.masked.push(flame)
        }

        if (player && player.appearance) {
            // Paperdoll off a synthetic entity carrying just appearance/inventory/weapon, the same
            // way the inventory screen and the roster build theirs — nothing here should touch the
            // live player entity.
            const doll = new Entity()
            doll.weapon = { weaponType: locateInventoryActiveItem(player.inventory, "weapon")?.type as any }
            doll.appearance = player.appearance
            doll.inventory = player.inventory

            const portrait = new LpcLayered(player.entityId)
            try {
                portrait.updatePaperdoll(doll)
                // Framing matched to the roster cards (same zoom + head-and-shoulders crop, scaled
                // up for this bigger card), so the player reads as one of the camp.
                portrait.scale.set(PORTRAIT_SCALE)
                portrait.x = PORTRAIT_W / 2
                portrait.y = Math.round(PORTRAIT_H * 0.57)
                // Sunk into the dark rather than deleted: the shape of the player is still there
                // to be made out, which is what makes it read as a body and not an empty frame.
                // The falling shadow below does most of that work, so this only takes the edge off.
                portrait.alpha = extinguished ? 0.72 : 1
                const portraitClip = makeClip()
                card.addChild(portraitClip)
                portrait.mask = portraitClip
                card.addChild(portrait)
                this.masked.push(portrait)
            } catch (e) {
                // A malformed paperdoll must not cost the player their only way back into a run.
                console.warn("[RunSummary] portrait build failed", e)
            }
        }

        // A cold shadow over the card once the fire behind it is out — drawn above the paperdoll so
        // it falls ON the player, and under the name plate so the name stays readable.
        //
        // It DEEPENS down the card rather than lying flat: the head keeps just enough light to be
        // recognisable and everything below it goes into the dark, which is what makes the card
        // read as a body sinking rather than a picture someone turned the brightness down on.
        if (extinguished) {
            const shadow = new Graphics()
            const top = 3
            const height = PORTRAIT_H - 6
            for (let band = 0; band < ASH_SHADOW_BANDS; band++) {
                // Eased, so the fall is gentle across the face and steep past the shoulders.
                const t = band / (ASH_SHADOW_BANDS - 1)
                const alpha = ASH_SHADOW_TOP + (ASH_SHADOW_BOTTOM - ASH_SHADOW_TOP) * Math.pow(t, 1.6)
                shadow.beginFill(ASH_SHADOW, alpha)
                // Half a pixel of overlap: abutting bands leave hairline seams once the panel is
                // scaled onto the stage.
                shadow.drawRect(top, top + (height * band) / ASH_SHADOW_BANDS, PORTRAIT_W - 6, height / ASH_SHADOW_BANDS + 0.6)
                shadow.endFill()
            }
            // Banded rects have square corners; the card does not. Clip them to the same inset
            // rounded rect the flame and the paperdoll use.
            const shadowClip = makeClip()
            card.addChild(shadowClip)
            shadow.mask = shadowClip
            card.addChild(shadow)
            this.masked.push(shadow)
        }

        // Scrim + name plate across the bottom of the card.
        const plate = new Graphics()
        plate.beginFill(0x000000, 0.55)
        plate.drawRoundedRect(3, PORTRAIT_H - 25, PORTRAIT_W - 6, 22, 6)
        plate.endFill()
        card.addChild(plate)

        const name = player?.name ? player.name : "Camper"
        this.addText(card, name.length > 14 ? `${name.slice(0, 13)}…` : name, {
            size: 11,
            bold: true,
            color: extinguished ? ASH_TEXT : TEXT_COLOR,
            x: PORTRAIT_W / 2,
            y: PORTRAIT_H - 21,
            center: true,
        })
    }

    // The three headline numbers. These are the run's score, so they get the panel's biggest type.
    private buildHeroStats(parent: Container, summary: EmberRunSummary) {
        const cells: Array<{ value: string; label: string; color: number }> = [
            { value: formatDuration(summary.survivedMs), label: "SURVIVED", color: AMBER },
            { value: `${summary.wavesCleared}`, label: "WAVES HELD", color: GOLD },
            { value: `${summary.killsTotal}`, label: "VANQUISHED", color: EMBER },
        ]
        const gap = 8
        const cellW = (STATS_W - gap * (cells.length - 1)) / cells.length

        cells.forEach((cell, i) => {
            const x = STATS_LEFT + i * (cellW + gap)
            const bg = new Graphics()
            bg.lineStyle(1, cell.color, 0.28)
            bg.beginFill(CARD_FILL, 0.85)
            bg.drawRoundedRect(x, HERO_TOP, cellW, HERO_H, 8)
            bg.endFill()
            parent.addChild(bg)

            this.addFittedValue(parent, cell.value, cell.color, x + cellW / 2, HERO_TOP + 9, cellW - 10)
            this.addText(parent, cell.label, {
                size: 8,
                alpha: 0.7,
                x: x + cellW / 2,
                y: HERO_TOP + HERO_H - 15,
                center: true,
            })
        })

        // The one number that is purely the player's own doing, rather than the camp's.
        if (summary.killsByPlayer > 0) {
            this.addText(parent, `${summary.killsByPlayer} struck down by your own hand`, {
                size: 9,
                alpha: 0.6,
                x: STATS_LEFT + STATS_W / 2,
                y: HERO_TOP + HERO_H + 6,
                center: true,
            })
        }
    }

    // What the player built and kept alive, as opposed to what they killed.
    private buildCampTiles(parent: Container, summary: EmberRunSummary) {
        const tiles: Array<{ emoji: string; value: number; label: string; color: number }> = [
            { emoji: "🪓", value: summary.treesFelled, label: "TREES FELLED", color: GREEN },
            { emoji: "🤝", value: summary.recruitsHired, label: "RECRUITED", color: GOLD },
            { emoji: "🔥", value: summary.fireRekindled, label: "FIRE FED", color: AMBER },
        ]
        const gap = 8
        const tileW = (STATS_W - gap * (tiles.length - 1)) / tiles.length

        tiles.forEach((tile, i) => {
            const x = STATS_LEFT + i * (tileW + gap)
            const bg = new Graphics()
            bg.lineStyle(1, CARD_RIM, 0.5)
            bg.beginFill(CARD_FILL, 0.6)
            bg.drawRoundedRect(x, TILE_TOP, tileW, TILE_H, 7)
            bg.endFill()
            parent.addChild(bg)

            // Emoji keeps its own colours (never tinted), so it sits on its own line to the left of
            // the count rather than inside the coloured value text.
            const emoji = new TextRenderer().fontSize(13).align("center").render(tile.emoji) as Text
            const count = new TextRenderer()
                .fontSize(15)
                .color(tile.color)
                .bold()
                .align("center")
                .render(`${tile.value}`) as Text
            const groupW = emoji.width + 4 + count.width
            emoji.x = Math.round(x + tileW / 2 - groupW / 2)
            emoji.y = TILE_TOP + 7
            count.x = Math.round(emoji.x + emoji.width + 4)
            count.y = TILE_TOP + 6
            parent.addChild(emoji, count)

            this.addText(parent, tile.label, {
                size: 7,
                alpha: 0.65,
                x: x + tileW / 2,
                y: TILE_TOP + TILE_H - 13,
                center: true,
            })
        })
    }

    // The kill breakdown, as compact emoji chips that flow onto as many rows as they need. Ordered
    // biggest-first by the server, so the run's defining enemy leads.
    private buildKillChips(parent: Container, summary: EmberRunSummary) {
        this.addText(parent, summary.kills.length > 0 ? "THE FALLEN" : "NOTHING FELL TO THE CAMP", {
            size: 8,
            alpha: 0.5,
            x: STATS_LEFT,
            y: CHIP_LABEL_Y,
        })

        let x = STATS_LEFT
        let y = CHIP_TOP
        summary.kills.forEach(kill => {
            const meta = creatureMeta(kill.key)
            const emoji = new TextRenderer().fontSize(10).align("center").render(meta.emoji) as Text
            const label = new TextRenderer()
                .fontSize(9)
                .color(TEXT_COLOR)
                .align("center")
                .render(`${meta.label} ${kill.count}`) as Text
            const chipW = 8 + emoji.width + 4 + label.width + 8

            // Wrap to the next row; drop anything that would spill past the two rows the panel has
            // room for (a long tail of one-off kinds isn't worth pushing the button off the panel).
            if (x + chipW > STATS_LEFT + STATS_W) {
                x = STATS_LEFT
                y += CHIP_H + CHIP_GAP
            }
            if (y + CHIP_H > BUTTON_TOP - 6) {
                disposeTextTextures(emoji)
                disposeTextTextures(label)
                return
            }

            const bg = new Graphics()
            bg.lineStyle(1, CARD_RIM, 0.55)
            bg.beginFill(CARD_FILL, 0.75)
            bg.drawRoundedRect(x, y, chipW, CHIP_H, CHIP_H / 2)
            bg.endFill()
            parent.addChild(bg)

            emoji.x = Math.round(x + 8)
            emoji.y = Math.round(y + 2)
            label.x = Math.round(emoji.x + emoji.width + 4)
            label.y = Math.round(y + 3)
            label.alpha = 0.9
            parent.addChild(emoji, label)

            x += chipW + CHIP_GAP
        })
    }

    // The lives this camp granted, as embers under the portrait: lit for a life still in hand,
    // burnt out for one already spent. The count is also spelled out underneath, because the
    // difference between two lit embers and three is not something to make anyone squint at.
    private buildLives(parent: Container, summary: EmberRunSummary) {
        const { total, spent, remaining, caption } = livesView(summary)
        if (total <= 0) {
            return
        }

        const columnCx = PAD + PORTRAIT_W / 2
        this.addText(parent, "LIVES", { size: 8, alpha: 0.5, x: columnCx, y: LIVES_LABEL_Y, center: true })

        const step = PIP_R * 2 + PIP_GAP
        const rowW = total * PIP_R * 2 + (total - 1) * PIP_GAP
        this.embers = new Graphics()
        parent.addChild(this.embers)
        this.livesLayout = {
            firstCx: columnCx - rowW / 2 + PIP_R,
            step,
            cy: LIVES_ROW_Y + PIP_R,
            total,
            spent,
        }
        // The row empties from the RIGHT: the lives still in hand stay banked on the left, and the
        // one this death just took is the leftmost of the dark ones -- which is the one that burns
        // out in front of the player (see drawLivesRow / animate).
        this.livesBurnMs = 0
        this.sparks = []
        this.drawLivesRow()

        this.addText(parent, caption, {
            size: 8,
            color: remaining > 0 ? TEXT_COLOR : EMBER,
            alpha: remaining > 0 ? 0.6 : 0.9,
            x: columnCx,
            y: LIVES_CAPTION_Y,
            center: true,
        })
    }

    // Redraw the whole row for the current moment of the burn-out. Cheap enough to do every frame
    // (a handful of filled paths), and it keeps all the state in one place: which lives are lit,
    // which are ash, and how far through going out the one in between is.
    private drawLivesRow() {
        const g = this.embers
        const layout = this.livesLayout
        if (!g || !layout) {
            return
        }
        const { firstCx, step, cy, total, spent } = layout
        const remaining = total - spent
        // How much fire is left in the life this death took. It holds — guttering, which is what
        // the flicker is — and only then goes out, so the loss reads as an event rather than as a
        // value that was always that way. The one that burns is the leftmost of the spent ones:
        // the boundary between what is still in hand and what is gone.
        const t = Math.max(0, Math.min(1, this.livesBurnMs / LIVES_BURN_MS))
        const heat =
            t < LIVES_BURN_HOLD
                ? 1 - 0.14 * Math.abs(Math.sin(t * 26))
                : 1 - (t - LIVES_BURN_HOLD) / (1 - LIVES_BURN_HOLD)

        g.clear()
        for (let i = 0; i < total; i++) {
            const cx = firstCx + i * step
            if (i < remaining) {
                this.drawEmber(g, cx, cy, 1)
            } else if (i === remaining && spent > 0) {
                this.drawEmber(g, cx, cy, heat)
            } else {
                this.drawEmber(g, cx, cy, 0)
            }
        }

        this.sparks.forEach(spark => {
            const left = 1 - spark.age / spark.life
            if (left <= 0) {
                return
            }
            g.lineStyle(0)
            g.beginFill(EMBER_CORE, 0.85 * left)
            g.drawCircle(spark.x, spark.y, 1.4 * left + 0.5)
            g.endFill()
        })
    }

    // One ember: a teardrop flame, drawn at `heat` — 1 burning, 0 cold ash. The socket (the dark
    // outline) is always there, so the row keeps its shape as it empties; what changes is the fire
    // inside it, which shrinks into the socket and drains from red-orange to nothing.
    private drawEmber(g: Graphics, cx: number, cy: number, heat: number) {
        const flame = (r: number, oy: number = 0) => {
            const y = cy + oy
            g.moveTo(cx, y - r * 1.35)
            g.bezierCurveTo(cx + r * 1.0, y - r * 0.3, cx + r * 0.9, y + r * 0.85, cx, y + r)
            g.bezierCurveTo(cx - r * 0.9, y + r * 0.85, cx - r * 1.0, y - r * 0.3, cx, y - r * 1.35)
        }

        // The cold socket, under everything.
        g.lineStyle(1.5, CARD_RIM, 0.75)
        g.beginFill(0x120d0a, 0.9)
        flame(PIP_R)
        g.endFill()

        if (heat <= 0.01) {
            return
        }

        // A flame low on heat sits down into its socket rather than hovering, and its glow goes
        // first -- the light leaves the ember before the ember does.
        const scale = 0.35 + 0.65 * heat
        const drop = PIP_R * 0.5 * (1 - heat)

        g.lineStyle(0)
        g.beginFill(EMBER_BODY, 0.18 * heat)
        flame(PIP_R * 1.45 * scale, drop)
        g.endFill()

        g.lineStyle(1.5, EMBER_RIM, 0.95 * Math.min(1, heat * 1.4))
        g.beginFill(EMBER_BODY, 0.95)
        flame(PIP_R * scale, drop)
        g.endFill()

        g.lineStyle(0)
        g.beginFill(EMBER_CORE, 0.9 * heat)
        flame(PIP_R * 0.42 * scale, drop)
        g.endFill()
    }

    // Draw the ways on that runSummaryButtons decided there are (see run_summary_format: WHICH
    // buttons exist is the rule, and lives in the testable half). All this end does is size and
    // place them: the ordinary pair splits the panel's inner width, a lone one is wider and
    // centered rather than sitting in half a row with a gap beside it, and the minor one gets a row
    // of its own under the panel.
    private buildButtons(parent: Container) {
        const summary = this.summary
        if (!summary) {
            return
        }
        const buttons = runSummaryButtons(summary, this.invasionCandidates)
        const major = buttons.filter(button => !button.minor)
        const solo = major.length === 1

        buttons.forEach(button => {
            const i = major.indexOf(button)
            this.addButton(parent, {
                x: button.minor
                    ? PANEL_W / 2 - MINOR_BUTTON_W / 2
                    : solo
                      ? PANEL_W / 2 - SOLO_BUTTON_W / 2
                      : PAD + i * (BUTTON_W + BUTTON_GAP),
                y: button.minor ? MINOR_BUTTON_TOP : BUTTON_TOP,
                width: button.minor ? MINOR_BUTTON_W : solo ? SOLO_BUTTON_W : BUTTON_W,
                height: button.minor ? MINOR_BUTTON_H : BUTTON_H,
                label: button.label,
                sublabel: button.sublabel,
                primary: button.primary,
                minor: button.minor,
                enabled: button.enabled,
                onPress: () =>
                    button.action === "tryAgain"
                        ? this.onTryAgain?.()
                        : button.action === "restart"
                          ? this.onRestart?.()
                          : this.onInvade?.(),
            })
        })
    }

    // Re-run the button rule and redraw the row if its answer has changed. Cheap and idempotent, so
    // it can be called from the feed, and it is: the invasion offer arrives every couple of seconds
    // while the panel is up, which is what makes the third button light up by itself when a camp
    // opens. Guarded on a signature so a tick that changes nothing does not rebuild the row (and
    // lose the hover) under a pointer already resting on it.
    private syncButtons() {
        const summary = this.summary
        const layer = this.buttonLayer
        if (!summary || !layer) {
            return
        }
        const key = runSummaryButtons(summary, this.invasionCandidates)
            .map(button => `${button.action}/${button.enabled}/${button.sublabel}`)
            .join("|")
        if (key === this.buttonsKey) {
            return
        }
        this.buttonsKey = key
        // The handles point into the subtree being thrown away, so they go with it.
        this.buttons = []
        disposeTextTextures(layer)
        layer.removeChildren().forEach(child => child.destroy({ children: true }))
        this.buildButtons(layer)
    }

    private addButton(
        parent: Container,
        opts: {
            x: number
            y: number
            width?: number
            height?: number
            label: string
            sublabel: string
            primary: boolean
            minor?: boolean
            enabled?: boolean
            onPress: () => void
        },
    ) {
        const width = opts.width === undefined ? BUTTON_W : opts.width
        const height = opts.height === undefined ? BUTTON_H : opts.height
        const minor = !!opts.minor
        const enabled = opts.enabled !== false
        const container = new Container()
        container.x = opts.x
        container.y = opts.y
        parent.addChild(container)

        const bg = new Graphics()
        container.addChild(bg)
        const handle: ButtonHandle = {
            bg,
            width,
            height,
            primary: opts.primary,
            minor,
            // Starts the arrival flare. Without this the first draw sees a finished one.
            flareMs: 0,
            pulseMs: 0,
            enabled,
            hovered: false,
        }
        this.buttons.push(handle)
        this.drawButton(handle)

        // A filled button needs dark text on it; an outlined one needs light. The minor row is
        // smaller on both lines, which is most of what makes it read as third.
        // Dark on anything FILLED -- the primary way on, and now the invade button, which is lit
        // rather than outlined. Pale text on an ember fill is the one combination that reads as
        // broken. (UNDEAD_RIM, the old cool outline colour, is what it used to be.)
        const labelColor = opts.primary || minor ? 0x2a1a0c : TEXT_COLOR
        const label = new TextRenderer()
            .fontSize(minor ? 11 : 14)
            .color(labelColor)
            .bold()
            .align("center")
            .render(opts.label) as Text
        label.x = Math.round(width / 2 - label.width / 2)
        label.y = minor ? 4 : 8
        label.alpha = enabled ? 1 : 0.45
        container.addChild(label)

        const sub = new TextRenderer()
            .fontSize(minor ? 7 : 8)
            .color(minor ? TEXT_COLOR : labelColor)
            .align("center")
            .render(opts.sublabel) as Text
        sub.x = Math.round(width / 2 - sub.width / 2)
        sub.y = minor ? 19 : 27
        sub.alpha = enabled ? (opts.primary ? 0.7 : 0.6) : 0.4
        container.addChild(sub)

        // An inert button is drawn and readable but is NOT a control: no pointer, no hover, no
        // press. It is there to say why it cannot be used, and to light up when that changes.
        if (!enabled) {
            return
        }
        container.interactive = true
        container.cursor = "pointer"
        container.hitArea = new Rectangle(0, 0, width, height)
        container.on("pointerover", () => {
            handle.hovered = true
            this.drawButton(handle)
        })
        container.on("pointerout", () => {
            handle.hovered = false
            this.drawButton(handle)
        })
        container.on("pointerdown", (event: any) => {
            event.stopPropagation()
            opts.onPress()
        })
    }

    private drawButton(handle: ButtonHandle) {
        const g = handle.bg
        if (!g) {
            return
        }
        g.clear()

        // The minor row -- the way back into somebody else's night, and now the loudest thing on
        // this panel rather than the quietest.
        //
        // It started as a cool outline on dark glass, deliberately understated, on the reasoning
        // that the offer should be offered and not recommended. That was the wrong read once it was
        // played: by the time this button exists the player is sitting on a dead run looking at
        // numbers, and the one live thing on the screen has to say so.
        //
        // Three things carry it, and none of them is a pixi filter (see the GUI playbook -- a glow
        // is faked with concentric strokes, which also costs nothing):
        //
        //   1. An ARRIVAL FLARE. It is the only button that turns up mid-panel, so for the first
        //      moment it is oversized and white-hot, settling into its resting look. Movement on a
        //      still screen beats any colour.
        //   2. A BREATHING GLOW, so it keeps the eye after the flare is gone -- an ember, not a
        //      highlight, which is the one visual language this whole panel already speaks.
        //   3. A HOT FILL with dark text, like the primary button above it, rather than dark glass
        //      with pale text like everything else down here.
        if (handle.minor) {
            const flare = invadeFlare(handle.flareMs ?? INVADE_FLARE_MS)
            const breath = 0.5 + 0.5 * Math.sin(((handle.pulseMs ?? 0) / INVADE_BREATH_MS) * Math.PI * 2)
            // Hover tops the breath out rather than adding a separate state: the button is already
            // pulsing, so a hovered one simply stops dimming.
            const heat = handle.hovered ? 1 : 0.55 + 0.45 * breath

            // The fake glow: three rings, widest first, the outermost one riding the flare out.
            const spread = 4 + 7 * heat + 16 * flare
            for (let ring = 3; ring >= 1; ring--) {
                const t = ring / 3
                const pad = spread * t
                g.beginFill(ring === 3 ? EMBER_BODY : EMBER_RIM, (0.1 + 0.22 * heat) * (1 - t * 0.55))
                g.drawRoundedRect(-pad, -pad, handle.width + pad * 2, handle.height + pad * 2, 9 + pad * 0.5)
                g.endFill()
            }

            g.lineStyle(2, flare > 0 ? EMBER_CORE : EMBER_RIM, 1)
            g.beginFill(EMBER_BODY, 0.82 + 0.18 * heat)
            g.drawRoundedRect(0, 0, handle.width, handle.height, 7)
            g.endFill()

            // The flare's own white core, laid over the fill and gone within the beat.
            if (flare > 0) {
                g.beginFill(EMBER_CORE, 0.75 * flare)
                g.drawRoundedRect(0, 0, handle.width, handle.height, 7)
                g.endFill()
            }
            return
        }

        g.beginFill(RIM, handle.hovered ? 0.3 : 0.16)
        g.drawRoundedRect(-4, -4, handle.width + 8, handle.height + 8, 12)
        g.endFill()

        if (handle.primary) {
            g.lineStyle(2, handle.hovered ? 0xffe3ab : RIM, 1)
            g.beginFill(handle.hovered ? 0xffc978 : RIM, 1)
        } else {
            g.lineStyle(2, handle.hovered ? 0xffe3ab : RIM, handle.hovered ? 1 : 0.8)
            g.beginFill(CARD_FILL, handle.hovered ? 0.95 : 0.8)
        }
        g.drawRoundedRect(0, 0, handle.width, handle.height, 8)
        g.endFill()
    }

    // ===== public API ==================================================================

    public isOpen(): boolean {
        return this.opened
    }

    // The newest invasion offer off the feed (Protocol.type_updateInvasionOffer). Held whether or
    // not the panel is up, because the panel may go up a frame later and the third button has to be
    // on it immediately; `null` forgets the offer entirely, which takes the button away again.
    //
    // Note that an EMPTY array is not the same as null here. It is a real answer -- "no camp is
    // open to the dead" -- and it keeps an inert button on the panel saying so. See
    // runSummaryButtons.
    public setInvasionOffer(candidates?: EmberInvasionCandidate[] | null) {
        this.invasionCandidates = candidates || undefined
        this.syncButtons()
    }

    // What the panel's buttons currently say, for a caller outside the display tree. The RULE is in
    // run_summary_format (and tested there); this is only the drawn answer.
    public buttonSignature(): string | undefined {
        return this.buttonsKey
    }

    // Rebuild the whole panel around a new scorecard. `player` is the local player entity, used for
    // the portrait and name; the panel still renders (without a portrait) if it's unavailable.
    // NOT named `render` -- that's pixi's own DisplayObject.render(renderer).
    public setSummary(summary: EmberRunSummary, player?: Entity) {
        this.teardownContent()
        this.summary = summary

        const content = new Container()
        this.content = content
        this.panel.addChild(content)

        content.addChild(this.drawPanelChrome())

        this.addText(content, "YOU HAVE FALLEN", {
            size: 18,
            color: RIM,
            bold: true,
            x: PANEL_W / 2,
            y: 12,
            center: true,
        })
        this.addText(
            content,
            summary.wavesReached > 0
                ? `You held the ember through wave ${summary.wavesReached}.`
                : "The ember went dark before the first wave.",
            { size: 9, alpha: 0.6, x: PANEL_W / 2, y: 33, center: true },
        )

        // With the lives spent the camp is finished with this player, and the portrait card says
        // so: its fire is out (see buildPortrait).
        this.buildPortrait(content, player, !livesView(summary).canTryAgain)
        this.buildHeroStats(content, summary)
        this.buildCampTiles(content, summary)
        this.buildKillChips(content, summary)
        this.buildLives(content, summary)

        // The ways on go in a layer of their own, because they are the one part of this panel that
        // is rebuilt while it is up (see syncButtons).
        this.buttonLayer = new Container()
        content.addChild(this.buttonLayer)
        this.buttonsKey = undefined
        this.syncButtons()

        // ...and put the flourish in front of all of it. The panel is not merely transparent
        // while the number is up, it is HIDDEN: an invisible button is still a pressable one, and
        // a player mashing attack as they die must not be able to restart a run they haven't been
        // shown yet.
        this.startReveal(summary)
    }

    // ===== the opening flourish ========================================================

    // Arm the wave reveal and put the panel in its pre-entrance state. Everything after this is
    // driven from animate(), off waveRevealFrame's clock.
    private startReveal(summary: EmberRunSummary) {
        this.revealMs = 0
        this.revealing = true
        this.reveal.setSummary(summary)
        this.panel.visible = false
        this.panel.alpha = 0
        this.panel.y = PANEL_TOP
    }

    // Advance it a frame, hand the panel its share of the movement, and take the whole thing down
    // once the beat is over. From here on the panel is simply the panel.
    private advanceReveal(dtMs: number) {
        // Clamped, because this beat is measured in tenths of a second and one stalled frame (a
        // tab coming back, a texture upload) can be longer than the whole of it -- which would
        // land the player on the panel having never seen the number.
        const step = Math.max(0, Math.min(dtMs, REVEAL_MAX_STEP_MS))
        this.revealMs += step
        const frame: WaveRevealFrame = waveRevealFrame(this.revealMs)
        this.reveal.applyFrame(frame, step)

        this.panel.visible = frame.panelAlpha > 0
        this.panel.alpha = frame.panelAlpha
        this.panel.y = PANEL_TOP + frame.panelRise

        if (frame.done) {
            this.endReveal()
        }
    }

    // Leave the panel exactly as it would have been had there been no flourish at all. Called both
    // when the beat finishes and when the panel is torn down mid-beat.
    private endReveal() {
        this.revealing = false
        this.revealMs = 0
        this.reveal.teardown()
        this.panel.visible = true
        this.panel.alpha = 1
        this.panel.y = PANEL_TOP
    }

    private teardownContent() {
        if (this.revealing) {
            this.endReveal()
        }
        this.masked.forEach(node => {
            node.mask = null
        })
        this.masked = []
        this.flame = undefined
        if (this.content) {
            this.panel.removeChild(this.content)
            disposeTextTextures(this.content)
            this.content.destroy({ children: true })
        }
        this.buttons = []
        this.buttonLayer = undefined
        this.buttonsKey = undefined
        this.summary = undefined
        // NOT the invasion candidates: the feed is about the SERVER's state, not this panel's, and
        // a panel that forgot the newest list on teardown would come back up with no third button
        // until the next tick -- up to two seconds of the offer being missing at the exact moment
        // it is meant to be read.
        this.embers = undefined
        this.livesLayout = undefined
        this.sparks = []
    }

    // Called each frame by GuiManager: the opening flourish, the portrait's campfire particles, and
    // the life this death took guttering out. Note the flame is optional (no portrait without a
    // player entity) while the lives are not, so neither may gate the other.
    public animate(dtMs: number) {
        if (!this.visible) {
            return
        }
        if (this.revealing) {
            this.advanceReveal(dtMs)
        }
        this.flame?.update(dtMs, 0.85)
        this.advanceLivesBurn(dtMs)
        this.advanceInvadeHeat(dtMs)
    }

    // The invade button breathes, and flares once when it appears. Redrawn every frame it is on
    // screen, which is one Graphics with a handful of rounded rects -- the panel is already
    // redrawing the lives embers at this rate.
    private advanceInvadeHeat(dtMs: number) {
        this.buttons.forEach(handle => {
            if (!handle.minor) {
                return
            }
            handle.pulseMs = ((handle.pulseMs ?? 0) + dtMs) % INVADE_BREATH_MS
            if ((handle.flareMs ?? INVADE_FLARE_MS) < INVADE_FLARE_MS) {
                handle.flareMs = Math.min(INVADE_FLARE_MS, (handle.flareMs ?? 0) + dtMs)
            }
            this.drawButton(handle)
        })
    }

    // The just-spent ember: it holds, flickering, long enough to be noticed, then collapses into
    // its socket and throws off a few sparks on the way out. Runs once per opening of the panel.
    private advanceLivesBurn(dtMs: number) {
        const layout = this.livesLayout
        if (!layout || layout.spent <= 0 || !this.embers) {
            return
        }
        // Not while the wave reveal is still up. The whole point of this animation is that the
        // player SEES the life go out, and the panel it is drawn on is hidden until the flourish
        // hands over -- left running, the ember would be ash by the time anyone looked at it.
        if (this.revealing && this.panel.alpha <= 0) {
            return
        }
        const finished = this.livesBurnMs >= LIVES_BURN_MS
        if (finished && this.sparks.length === 0) {
            return
        }

        const before = this.livesBurnMs
        this.livesBurnMs = Math.min(LIVES_BURN_MS, this.livesBurnMs + dtMs)

        // Sparks are thrown at the moment it starts to go out, not while it is still burning.
        const startMs = LIVES_BURN_MS * LIVES_BURN_HOLD
        if (before < startMs && this.livesBurnMs >= startMs) {
            const cx = layout.firstCx + (layout.total - layout.spent) * layout.step
            for (let i = 0; i < 6; i++) {
                this.sparks.push({
                    x: cx + (Math.random() - 0.5) * PIP_R * 1.2,
                    y: layout.cy - PIP_R * 0.4,
                    vx: (Math.random() - 0.5) * 0.012,
                    vy: -(0.02 + Math.random() * 0.02),
                    age: 0,
                    life: 420 + Math.random() * 320,
                })
            }
        }

        this.sparks = this.sparks.filter(spark => {
            spark.age += dtMs
            spark.x += spark.vx * dtMs
            spark.y += spark.vy * dtMs
            return spark.age < spark.life
        })

        this.drawLivesRow()
    }

    public fadeIn() {
        this.opened = true

        if (this.fadeOutFader) {
            this.fadeOutFader.stop(true)
            this.fadeOutFader = null
        }
        if (this.fadeInFader) {
            return
        }

        if (!this.visible) {
            this.alpha = 0
        }
        this.visible = true

        if (this.alpha >= 1) {
            this.alpha = 1
            return
        }

        this.fadeInFader = new Fader(this, {
            direction: "in",
            minAlpha: 1.0,
            fadeInterval: FADE_INTERVAL_MS,
            fadeAlphaDecrement: FADE_STEP,
            releaseCallback: () => {
                this.fadeInFader = null
                this.alpha = 1
            },
        })
        this.fadeInFader.update()
    }

    public fadeOut() {
        this.opened = false
        // Drop every hover highlight: the pointer is nowhere near these buttons the next time the
        // panel opens, and a button that came up already lit would read as pre-selected.
        this.buttons.forEach(button => {
            button.hovered = false
        })

        if (this.fadeInFader) {
            this.fadeInFader.stop(true)
            this.fadeInFader = null
        }
        if (this.fadeOutFader || !this.visible) {
            return
        }

        this.fadeOutFader = new Fader(this, {
            direction: "out",
            minAlpha: 0.0,
            fadeInterval: FADE_INTERVAL_MS,
            fadeAlphaDecrement: FADE_STEP,
            releaseCallback: () => {
                this.fadeOutFader = null
                this.visible = false
                this.alpha = 1.0
                // The run is over and the panel is gone — free the paperdoll/text textures rather
                // than hold a whole scorecard's worth until the next death.
                this.teardownContent()
            },
        })
        this.fadeOutFader.update()
    }
}
